import React, { useState, useEffect, useCallback } from 'react';
import './App.css'; // Import the separate CSS file

// Configuration for Azure Blob Storage
const AZURE_CONFIG = {
  storageAccount: 'swarnaastra',
  containerName: 'gallery',
  // Option 1: Use a long-lived SAS token with read-only permissions
  // Generate this from Azure Portal with 1+ year expiry
  sasToken: 'sp=r&st=2025-12-31T15:34:36Z&se=2026-12-31T23:49:36Z&sv=2024-11-04&sr=c&sig=G8iwqvhE1JzxcHw4YBCXK5PVbJifUQSnWINDSwapExY%3D',
  
  // Option 2: Photo filenames (if you know them in advance)
  photoFilenames: [
    'sept1.jpg',
    'sept2.jpg',
    'sept3.jpg',
    'sept4.jpg',
    'sept5.jpg',
    'sept6.jpg',
    'sept7.jpg',
    'sept9.jpg',
    'sept10.jpg',
    'IMG-20250602-WA0019.jpg',
    'IMG-20250603-WA0012.jpg',
    'IMG-20250610-WA0024.jpg',
    'IMG-20250610-WA0026.jpg',
    'IMG-20250610-WA0028.jpg',
    'IMG-20250617-WA0015.jpg',
    'IMG-20250617-WA0017.jpg',
    'IMG-20250619-WA0009.jpg',
    'IMG-20250619-WA0011.jpg',
    'IMG-20250622-WA0020.jpg',
    'IMG-20250619-WA0012.jpg',
    'IMG-20250619-WA0013.jpg',
    'IMG-20250619-WA0014.jpg',
    'IMG-20250619-WA0015.jpg',
    'IMG-20250619-WA0016.jpg',
    'IMG-20250619-WA0017.jpg',
    'IMG-20250619-WA0054.jpg',
    'IMG-20250620-WA0019.jpg',
    'IMG-20250621-WA0014.jpg',
    'IMG-20250622-WA0021.jpg',
    
    
  ],
  faviconFilename: 'favicon.png'
};

// Fallback favicon as base64 SVG
const FALLBACK_FAVICON = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iOCIgZmlsbD0iIzY0NzQ4YiIvPgo8dGV4dCB4PSIxNiIgeT0iMjAiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSJib2xkIj5TPC90ZXh0Pgo8L3N2Zz4K';

function App() {
  const [photos, setPhotos] = useState([]);  
  const [faviconUrl, setFaviconUrl] = useState(FALLBACK_FAVICON); // Initialize with fallback
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [particles, setParticles] = useState([]);
  const [orbs, setOrbs] = useState([]);
  const [waves, setWaves] = useState([]);

  // Generate Azure Blob URL with SAS token
  const generateBlobUrl = useCallback((filename) => {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.blob.core.windows.net/${AZURE_CONFIG.containerName}/${filename}`;
    return `${baseUrl}?${AZURE_CONFIG.sasToken}`;
  }, []);

  const getFaviconUrl = useCallback(() => {
    return generateBlobUrl(AZURE_CONFIG.faviconFilename);
  }, [generateBlobUrl]);
  // Load favicon and set up fallback
  useEffect(() => {
    const loadFavicon = async () => {
      try {
        const faviconSrc = getFaviconUrl();
        
        // Test if the favicon loads successfully
        const img = new Image();
        img.onload = () => {
          setFaviconUrl(faviconSrc);
        };
        img.onerror = () => {
          console.warn('Favicon failed to load from Azure, using fallback');
          setFaviconUrl(FALLBACK_FAVICON);
        };
        img.src = faviconSrc;
      } catch (err) {
        console.warn('Error loading favicon:', err);
        setFaviconUrl(FALLBACK_FAVICON);
      }
    };

    loadFavicon();
  }, [getFaviconUrl]);

  // Load photos on component mount
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        setLoading(true);
        
        // Method 1: Use predefined filenames
        const photoUrls = AZURE_CONFIG.photoFilenames.map(filename => 
          generateBlobUrl(filename)
        );
        
        setPhotos(photoUrls);
        setError(null);
      } catch (err) {
        console.error('Error loading photos:', err);
        setError('Failed to load photos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [generateBlobUrl]);

  // Error handling for favicon - fallback is already set, so just log
  const handleFaviconError = useCallback((e) => {
    console.warn('Favicon failed to load, already using fallback');
    // Prevent further error events
    e.target.onError = null;
  }, []);

  const handleImageError = useCallback((e, index) => {
    console.warn(`Failed to load image at index ${index}:`, e.target.src);
    // Hide failed images
    e.target.style.display = 'none';
  }, []);

  const handleOverlayClick = useCallback(() => {
    setSelected(null);
  }, []);

  const handleImageClick = useCallback((src) => {
    setSelected(src);
  }, []);

  // Scroll to top function for logo clicks
  const handleLogoClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle keyboard events for gallery images
  const handleImageKeyDown = useCallback((e, src) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleImageClick(src);
    }
  }, [handleImageClick]);

  // Handle keyboard events for overlay
  const handleOverlayKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleOverlayClick();
    }
  }, [handleOverlayClick]);

  // Initialize animations
  useEffect(() => {
    // Enhanced floating particles
    const newParticles = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      speed: Math.random() * 0.8 + 0.2,
      opacity: Math.random() * 0.4 + 0.1,
      color: i % 3 === 0 ? '#64748b' : i % 3 === 1 ? '#475569' : '#334155',
    }));
    setParticles(newParticles);

    // Floating orbs
    const newOrbs = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 120 + 60,
      speed: Math.random() * 0.3 + 0.1,
      opacity: Math.random() * 0.05 + 0.02,
      color: ['#64748b', '#475569', '#334155', '#1e293b'][i % 4],
    }));
    setOrbs(newOrbs);

    // Wave patterns
    const newWaves = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      amplitude: Math.random() * 20 + 10,
      frequency: Math.random() * 0.02 + 0.01,
      phase: Math.random() * Math.PI * 2,
      opacity: Math.random() * 0.1 + 0.05,
      color: ['#64748b', '#475569', '#334155'][i % 3],
    }));
    setWaves(newWaves);

    // Update particles and orbs periodically
    const interval = setInterval(() => {
      setOrbs(prev => prev.map(orb => ({
        ...orb,
        x: (orb.x + orb.speed) % 100,
        y: orb.y + Math.sin(Date.now() * 0.001 + orb.id) * 0.1,
      })));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app">
      {/* Enhanced Background Animations */}
      <div className="bg-animation">
        {/* Enhanced Grid */}
        <div className="enhanced-grid"></div>
        
        {/* Floating Orbs */}
        <div className="orbs">
          {orbs.map((orb) => (
            <div
              key={orb.id}
              className="orb"
              style={{
                left: `${orb.x}%`,
                top: `${orb.y}%`,
                width: `${orb.size}px`,
                height: `${orb.size}px`,
                background: orb.color,
                '--opacity': orb.opacity,
                animationDelay: `${orb.id * 0.5}s`,
                animationDuration: `${12 + orb.speed * 8}s`,
              }}
            />
          ))}
        </div>

        {/* Enhanced Particles */}
        <div className="particles">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                background: particle.color,
                '--opacity': particle.opacity,
                animationDelay: `${particle.id * 0.1}s`,
                animationDuration: `${10 + particle.speed * 5}s`,
              }}
            />
          ))}
        </div>

        {/* Wave Animation */}
        <div className="waves">
          {waves.map((wave) => (
            <div
              key={wave.id}
              className="wave"
              style={{
                top: `${20 + wave.id * 15}%`,
                background: `linear-gradient(90deg, transparent, ${wave.color}, transparent)`,
                opacity: wave.opacity,
                animationDelay: `${wave.id * 1.2}s`,
              }}
            />
          ))}
        </div>

        {/* Geometric Shapes */}
        <div className="geometric-shapes">
          <div className="geo-shape"></div>
          <div className="geo-shape"></div>
          <div className="geo-shape"></div>
        </div>
      </div>

      {/* Professional Header */}
      <header className="header">
        <div 
          onClick={handleLogoClick} 
          className="logo" 
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLogoClick();
            }
          }}
          aria-label="Swarna Astra - Scroll to top"
        >
          <img 
            src={faviconUrl} 
            alt="Swarna Astra" 
            className="logo-icon"
            onError={handleFaviconError} 
          />
          <span className="logo-text">Swarna Astra</span>
        </div>
        <nav className="nav">
          <a 
            href="https://forms.gle/myDYPSjeN7X1uvUy5" 
            className="contact-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Contact Us</span>
          </a>
        </nav>
      </header>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading gallery...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-container">
          <p className="error-text">{error}</p>
        </div>
      )}

      {/* Gallery */}
      <main className="gallery">
        {photos.map((src, index) => (
          <div
            key={index}
            className="photo-wrapper"
            onClick={() => handleImageClick(src)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleImageKeyDown(e, src)}
            aria-label={`View enlarged image ${index + 1}`}
          >
            <img
              src={src}
              alt={`Review ${index + 1}`}
              className="photo"
              onError={(e) => handleImageError(e, index)}
            />
          </div>
        ))}
      </main>

      {/* Overlay */}
      {selected && (
        <div 
          className="overlay" 
          onClick={handleOverlayClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleOverlayKeyDown}
          aria-label="Close enlarged image"
        >
          <img src={selected} alt="Enlarged" className="enlarged" />
        </div>
      )}

      {/* Professional Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <div 
              onClick={handleLogoClick} 
              className="footer-logo" 
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLogoClick();
                }
              }}
              aria-label="Swarna Astra - Scroll to top"
            >
              <img 
                src={faviconUrl} 
                alt="Swarna Astra" 
                className="footer-logo-icon"
                onError={handleFaviconError} 
              />
              <span className="footer-logo-text">Swarna Astra</span>
            </div>
          </div>
          <div className="footer-right">
            <div className="footer-copy">
              © 2025 Swarna Astra. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
